import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal, Pencil, UserPlus, XCircle, Mail, Phone, CalendarDays, MapPin, Briefcase, Users } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { SheetTitle } from '../../../ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { StageChip, initialsOf, fmtDate } from './drawerShared';

const Meta = ({ icon: Icon, label, value, testId }) => (
  <div className="flex min-w-0 items-start gap-2" data-testid={testId}>
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400">{label}</p>
      <p className={`truncate text-xs ${value ? 'font-medium text-slate-800' : 'text-slate-400'}`} title={typeof value === 'string' ? value : undefined}>{value || '—'}</p>
    </div>
  </div>
);

export const CandidateHeader = ({ candidate, canEdit, canConvert, canReject, onEdit, onConvert, onReject, nav, editing = false }) => {
  const location = [candidate.current_location, candidate.preferred_location && candidate.preferred_location !== candidate.current_location ? `→ ${candidate.preferred_location}` : null].filter(Boolean).join(' ');
  const showMenu = !editing && (canReject || canConvert);
  return (
    <div className="shrink-0 border-b border-slate-200 bg-white" data-testid="candidate-header">
      <div className="flex items-center justify-between gap-3 pl-6 pr-14 pt-4">
        {editing ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700" data-testid="drawer-edit-mode-chip">
            <Pencil className="h-3 w-3" /> Edit Mode
          </span>
        ) : (
        <div className="flex items-center gap-1.5" data-testid="candidate-nav">
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs text-slate-600" disabled={!nav?.hasPrev} onClick={nav?.onPrev} data-testid="drawer-prev-btn">
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </Button>
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs text-slate-600" disabled={!nav?.hasNext} onClick={nav?.onNext} data-testid="drawer-next-btn">
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          {nav?.position && <span className="ml-1 text-[11px] text-slate-400" data-testid="drawer-nav-position">{nav.position}</span>}
        </div>
        )}
        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs font-semibold" onClick={onEdit} data-testid="drawer-edit-profile-btn">
              <Pencil className="h-3.5 w-3.5" /> Edit Profile
            </Button>
          )}
          {showMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid="drawer-more-actions-btn" aria-label="More actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {canConvert && (
                  <DropdownMenuItem onClick={onConvert} className="gap-2 text-emerald-700 focus:text-emerald-800" data-testid="drawer-convert-btn">
                    <UserPlus className="h-4 w-4" /> Convert to Employee
                  </DropdownMenuItem>
                )}
                {canConvert && canReject && <DropdownMenuSeparator />}
                {canReject && (
                  <DropdownMenuItem onClick={onReject} className="gap-2 text-rose-600 focus:text-rose-700" data-testid="drawer-reject-btn">
                    <XCircle className="h-4 w-4" /> Reject Candidate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 pb-4 pt-3 md:flex-row md:items-start">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-50 text-lg font-bold text-blue-700 ring-4 ring-blue-50/60" data-testid="candidate-avatar">
          {initialsOf(candidate.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle className="sf-heading truncate text-xl font-bold tracking-tight text-slate-900" data-testid="drawer-candidate-name">{candidate.name}</SheetTitle>
            <StageChip stage={candidate.stage} data-testid="drawer-candidate-stage" />
            {candidate.is_converted && (
              <Badge variant="secondary" className="text-[11px]" data-testid="drawer-converted-badge">Converted → {candidate.converted_employee_id}</Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="font-mono text-[11px] text-slate-400" data-testid="drawer-candidate-id">{candidate.candidate_id}</span>
            <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5 text-slate-400" />{candidate.job_title || candidate.job_id || '—'}</span>
            {candidate.job_team && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" />{candidate.job_team}</span>}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-slate-100 pt-3 sm:grid-cols-4" data-testid="drawer-candidate-info">
            <Meta icon={Mail} label="Email" value={candidate.email} testId="header-email" />
            <Meta icon={Phone} label="Phone" value={candidate.phone} testId="header-phone" />
            <Meta icon={CalendarDays} label="Applied" value={fmtDate(candidate.created_at)} testId="header-applied" />
            <Meta icon={MapPin} label="Location" value={location} testId="header-location" />
          </div>
        </div>
      </div>
    </div>
  );
};
